import React from 'react';
import { Check, X, Minus } from 'lucide-react';

const FEATURES = [
  {
    label: 'Auction-style tendering (collect & compare all quotes)',
    vibe: true,
    other: 'partial',
    manual: false,
  },
  {
    label: 'Waterfall rate offers with auto-rollover',
    vibe: true,
    other: 'partial',
    manual: false,
  },
  {
    label: 'Critical / fast-cover strategy for urgent freight',
    vibe: true,
    other: false,
    manual: 'partial',
  },
  {
    label: 'Native integrations with Turvo, RoseRocket, NetSuite',
    vibe: true,
    other: 'partial',
    manual: false,
  },
  {
    label: 'TMS & ERP integrations',
    vibe: true,
    other: false,
    manual: false,
  },
  {
    label: 'Designed for shippers, not just brokers',
    vibe: true,
    other: false,
    manual: 'partial',
  },
  {
    label: 'Full audit log of every quote and award',
    vibe: true,
    other: 'partial',
    manual: false,
  },
];

const COLS = [
  { id: 'vibe', label: 'Vibe Engine', accent: true },
  { id: 'other', label: 'Other tools' },
  { id: 'manual', label: 'Manual process' },
];

export default function WhyVibe() {
  return (
    <section className="bg-white">
      <div className="container-page py-20 lg:py-28">
        <div className="max-w-3xl">
          <p className="eyebrow">Why Vibe</p>
          <h2 className="mt-3 h-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-balance">
            Built for the people actually moving freight.
          </h2>
          <p className="mt-5 text-lg text-ink-600 text-balance">
            Most automation tools were built for brokers buying capacity. Vibe
            Engine is built for shippers buying freight — with the strategies,
            integrations, and controls your team actually needs.
          </p>
        </div>

        <div className="mt-12 card-surface overflow-hidden">
          <div className="grid grid-cols-[1.4fr_repeat(3,minmax(0,1fr))] sm:grid-cols-[2fr_repeat(3,minmax(0,1fr))]">
            <div className="px-5 py-4 border-b border-ink-200 text-xs uppercase tracking-wider font-semibold text-ink-500">
              Capability
            </div>
            {COLS.map((c) => (
              <div
                key={c.id}
                className={`px-5 py-4 border-b border-ink-200 text-xs uppercase tracking-wider font-semibold ${
                  c.accent ? 'bg-ink-900 text-white' : 'text-ink-500'
                }`}
              >
                {c.label}
              </div>
            ))}

            {FEATURES.map((f, idx) => (
              <React.Fragment key={f.label}>
                <div
                  className={`px-5 py-4 text-sm text-ink-800 ${
                    idx % 2 === 1 ? 'bg-ink-50' : ''
                  } ${idx === FEATURES.length - 1 ? '' : 'border-b border-ink-200'}`}
                >
                  {f.label}
                </div>
                {COLS.map((c) => {
                  const v = f[c.id];
                  return (
                    <div
                      key={c.id}
                      className={`px-5 py-4 ${
                        c.accent ? 'bg-ink-900/95 text-white' : ''
                      } ${idx % 2 === 1 && !c.accent ? 'bg-ink-50' : ''} ${
                        idx === FEATURES.length - 1 ? '' : 'border-b border-ink-200'
                      }`}
                    >
                      <Mark v={v} accent={c.accent} />
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        </div>

        <p className="mt-5 text-xs text-ink-500">
          Comparison reflects typical capabilities of carrier-sourcing tools
          aimed at brokerages versus a manual tendering workflow.
        </p>
      </div>
    </section>
  );
}

function Mark({ v, accent }) {
  if (v === true) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-sm font-medium ${
          accent ? 'text-white' : 'text-ink-900'
        }`}
      >
        <Check className="h-4 w-4" />
        Yes
      </span>
    );
  }
  if (v === 'partial') {
    return (
      <span className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-600">
        <Minus className="h-4 w-4" />
        Partial
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-ink-400">
      <X className="h-4 w-4" />
      No
    </span>
  );
}
